import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { Buildings, PencilSimple, Trash, X, ArrowsClockwise, Lock, LockOpen, CheckCircle, Warning, Info } from "@phosphor-icons/react";

const METHODS = [
  { key: "manual", label: "Manual (no automated sync)" },
  { key: "url_scraper", label: "URL Scraper (paste product URLs)" },
  { key: "csv_feed", label: "CSV Feed" },
  { key: "xml_feed", label: "XML Feed" },
  { key: "json_feed", label: "JSON Feed" },
  { key: "http_api", label: "HTTP API" },
  { key: "veloconnect", label: "Veloconnect (bicycle industry)" },
  { key: "email_export", label: "Email export (manual upload)" },
];

const SECRET_FIELDS = [
  { key: "password", label: "Password / Kennwort" },
  { key: "api_key", label: "API Key" },
  { key: "api_token", label: "API Token" },
  { key: "token", label: "Access Token" },
  { key: "secret", label: "Client Secret" },
  { key: "client_secret", label: "OAuth Client Secret" },
];

export default function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showCredentials, setShowCredentials] = useState(null);
  const [showNotes, setShowNotes] = useState(null);
  const [testing, setTesting] = useState({});
  const [syncing, setSyncing] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/admin/suppliers")
      .then((r) => setSuppliers(r.data))
      .catch(() => toast.error("Could not load suppliers"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const patchSupplier = async (s, patch) => {
    try {
      await api.put(`/admin/suppliers/${s.id}`, patch);
      toast.success(`${s.name} updated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"? Products keep their manual data but lose this supplier's offer.`)) return;
    try {
      await api.delete(`/admin/suppliers/${s.id}`);
      toast.success("Supplier deleted"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const testConnection = async (s) => {
    setTesting({ ...testing, [s.id]: true });
    try {
      const { data } = await api.post(`/admin/suppliers/${s.id}/test-connection`);
      if (data.ready) toast.success(`Ready — ${data.message}`);
      else toast.error(`Not ready: missing ${data.missing.join(", ") || "config"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test failed");
    } finally { setTesting({ ...testing, [s.id]: false }); }
  };

  const syncNow = async (s) => {
    setSyncing({ ...syncing, [s.id]: true });
    try {
      const { data } = await api.post(`/admin/suppliers/${s.id}/sync`);
      toast(data.message || "Sync started", { duration: 6000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally { setSyncing({ ...syncing, [s.id]: false }); }
  };

  return (
    <div className="p-8 lg:p-12" data-testid="admin-suppliers">
      <div className="mb-6">
        <div className="overline text-[color:var(--vt-orange)] mb-2">/ B2B INTEGRATIONS</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Suppliers</h1>
        <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
          B2B supplier integrations for product import and price/stock sync. Each supplier can use its own
          integration method and markup — the retail price uses <strong>supplier purchase price × (1 + markup%)</strong>.
          Credentials are stored server-side and never exposed to the frontend.
        </p>
      </div>

      {loading && <div className="p-12 text-center text-neutral-500">Loading suppliers…</div>}

      {!loading && (
        <div className="grid md:grid-cols-2 gap-6" data-testid="suppliers-grid">
          {suppliers.map((s) => (
            <div key={s.id} className={`border bg-white ${s.active ? "border-green-400" : "border-neutral-200"}`} data-testid={`supplier-card-${s.key}`}>
              <div className="p-5 border-b border-neutral-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Buildings size={24} weight="duotone" className="text-[color:var(--vt-orange)]" />
                    <div>
                      <h3 className="font-display font-bold text-lg">{s.name}</h3>
                      {s.website_url && <a href={s.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-neutral-500 hover:underline">{s.website_url}</a>}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs uppercase font-bold cursor-pointer">
                    <input type="checkbox" checked={!!s.active} onChange={(e) => patchSupplier(s, { active: e.target.checked })} data-testid={`active-${s.key}`} />
                    {s.active ? "Active" : "Inactive"}
                  </label>
                </div>
                <div className="flex flex-wrap gap-3 text-[10px] uppercase tracking-widest text-neutral-500">
                  <span>Method: <strong className="text-black">{s.integration_method}</strong></span>
                  <span>Markup: <strong className="text-black">+{s.markup_pct}%</strong></span>
                  <span>Prices: <strong className="text-black">{s.price_is_net ? "NET" : "GROSS"}</strong></span>
                  <span>VAT: <strong className="text-black">{s.vat_rate}%</strong></span>
                </div>
              </div>

              <div className="p-5 grid grid-cols-2 gap-3 text-xs bg-neutral-50 border-b border-neutral-200">
                <div>
                  <div className="overline text-neutral-500 mb-1">Connection</div>
                  <div className={`inline-flex items-center gap-1 px-2 py-1 font-bold uppercase ${s.last_sync_status === "ok" ? "bg-green-100 text-green-800" : s.last_sync_status === "failed" ? "bg-red-100 text-red-700" : "bg-neutral-200 text-neutral-700"}`}>
                    {s.last_sync_status === "ok" ? <CheckCircle size={11} /> : s.last_sync_status === "failed" ? <Warning size={11} /> : <Info size={11} />}
                    {s.last_sync_status}
                  </div>
                </div>
                <div>
                  <div className="overline text-neutral-500 mb-1">Last sync</div>
                  <div>{s.last_sync_at ? new Date(s.last_sync_at).toLocaleString() : "—"}</div>
                </div>
                <div>
                  <div className="overline text-neutral-500 mb-1">Imported</div>
                  <div className="font-bold">{s.stats?.imported ?? 0}</div>
                </div>
                <div>
                  <div className="overline text-neutral-500 mb-1">Updated</div>
                  <div className="font-bold">{s.stats?.updated ?? 0}</div>
                </div>
                <div>
                  <div className="overline text-neutral-500 mb-1">Skipped</div>
                  <div>{s.stats?.skipped ?? 0}</div>
                </div>
                <div>
                  <div className="overline text-neutral-500 mb-1">Errors</div>
                  <div className={s.stats?.errors > 0 ? "text-red-600 font-bold" : ""}>{s.stats?.errors ?? 0}</div>
                </div>
              </div>

              {/* Credential badges */}
              <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2 flex-wrap text-[10px] uppercase font-bold tracking-widest">
                <span className="text-neutral-500">Credentials:</span>
                {SECRET_FIELDS.filter((f) => s.has_credentials?.[f.key]).length === 0 && <span className="text-neutral-400">— none set</span>}
                {SECRET_FIELDS.filter((f) => s.has_credentials?.[f.key]).map((f) => (
                  <span key={f.key} className="bg-green-100 text-green-800 px-2 py-0.5 inline-flex items-center gap-1"><Lock size={10} weight="fill" /> {f.label}</span>
                ))}
              </div>

              {s.last_error && (
                <div className="px-5 py-3 border-b border-neutral-200 bg-red-50 text-xs text-red-700" data-testid={`error-${s.key}`}>
                  <Warning size={12} weight="fill" className="inline mr-1" /> {s.last_error}
                </div>
              )}

              {s.notes && (
                <div className="px-5 py-3 border-b border-neutral-200 text-[11px] text-neutral-600">
                  <button onClick={() => setShowNotes(showNotes === s.id ? null : s.id)} className="text-[color:var(--vt-orange-hover)] hover:underline font-bold uppercase tracking-widest text-[10px]" data-testid={`toggle-notes-${s.key}`}>
                    {showNotes === s.id ? "Hide onboarding notes ↑" : "Show onboarding notes ↓"}
                  </button>
                  {showNotes === s.id && (
                    <pre className="mt-2 whitespace-pre-wrap font-sans text-[11px] leading-relaxed">{s.notes}</pre>
                  )}
                </div>
              )}

              <div className="p-5 flex flex-wrap gap-2">
                <button onClick={() => setEditing(s)} className="text-xs border border-black hover:bg-black hover:text-white px-3 py-2 uppercase font-bold tracking-widest transition-colors" data-testid={`edit-${s.key}`}>
                  <PencilSimple size={12} className="inline mr-1" /> Edit config
                </button>
                <button onClick={() => setShowCredentials(s)} className="text-xs border border-neutral-300 hover:border-black px-3 py-2 uppercase font-bold tracking-widest transition-colors" data-testid={`creds-${s.key}`}>
                  <Lock size={12} className="inline mr-1" /> Credentials
                </button>
                <button onClick={() => testConnection(s)} disabled={testing[s.id]} className="text-xs border border-neutral-300 hover:border-black px-3 py-2 uppercase font-bold tracking-widest transition-colors" data-testid={`test-${s.key}`}>
                  {testing[s.id] ? "Testing…" : "Test connection"}
                </button>
                <button onClick={() => syncNow(s)} disabled={syncing[s.id] || !s.active} className="vt-btn-orange text-xs disabled:opacity-40" title={!s.active ? "Activate supplier first" : ""} data-testid={`sync-${s.key}`}>
                  <ArrowsClockwise size={12} className={syncing[s.id] ? "animate-spin" : ""} /> Sync now
                </button>
                <button onClick={() => remove(s)} className="text-xs border border-red-300 text-red-600 hover:bg-red-600 hover:text-white px-3 py-2 uppercase font-bold tracking-widest transition-colors ml-auto" data-testid={`delete-${s.key}`}>
                  <Trash size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <ConfigEditor supplier={editing} onClose={() => { setEditing(null); load(); }} />}
      {showCredentials && <CredentialsEditor supplier={showCredentials} onClose={() => { setShowCredentials(null); load(); }} />}
    </div>
  );
}

// ------- Config editor modal -------
function ConfigEditor({ supplier, onClose }) {
  const [form, setForm] = useState({
    name: supplier.name, website_url: supplier.website_url || "",
    integration_method: supplier.integration_method,
    endpoint_url: supplier.endpoint_url || "",
    username: supplier.username || "",
    contact_email: supplier.contact_email || "",
    notes: supplier.notes || "",
    markup_pct: supplier.markup_pct ?? 30,
    price_is_net: supplier.price_is_net !== false,
    vat_rate: supplier.vat_rate ?? 19,
    currency: supplier.currency || "EUR",
    sync_frequency: supplier.sync_frequency || "daily",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/suppliers/${supplier.id}`, form);
      toast.success("Supplier updated");
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="config-editor">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white max-w-2xl w-full p-6 my-8 relative">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 p-2 hover:bg-neutral-100"><X size={20} /></button>
        <div className="overline text-[color:var(--vt-orange)] mb-1">Supplier configuration</div>
        <h2 className="font-display font-black text-2xl tracking-tighter mb-5">{supplier.name}</h2>

        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="overline text-neutral-600 block mb-1">Name</label>
            <input required className="vt-input" value={form.name} onChange={upd("name")} data-testid="supplier-name" />
          </div>
          <div>
            <label className="overline text-neutral-600 block mb-1">Website</label>
            <input className="vt-input" value={form.website_url} onChange={upd("website_url")} />
          </div>
          <div className="md:col-span-2">
            <label className="overline text-neutral-600 block mb-1">Integration method</label>
            <select className="vt-input" value={form.integration_method} onChange={upd("integration_method")} data-testid="supplier-method">
              {METHODS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="overline text-neutral-600 block mb-1">Endpoint URL (feed URL / API base / Veloconnect URL)</label>
            <input className="vt-input" value={form.endpoint_url} onChange={upd("endpoint_url")} placeholder="https://…" data-testid="supplier-endpoint" />
          </div>
          <div>
            <label className="overline text-neutral-600 block mb-1">Username / Kundennummer</label>
            <input className="vt-input" value={form.username} onChange={upd("username")} data-testid="supplier-username" />
          </div>
          <div>
            <label className="overline text-neutral-600 block mb-1">Contact email</label>
            <input type="email" className="vt-input" value={form.contact_email} onChange={upd("contact_email")} placeholder="service@…" />
          </div>
        </div>

        <div className="border-t border-neutral-200 pt-4 mt-4 mb-4">
          <div className="overline text-neutral-500 mb-3">Pricing & VAT</div>
          <div className="grid md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="overline text-neutral-600 block mb-1">Markup %</label>
              <input type="number" min="0" step="1" className="vt-input" value={form.markup_pct} onChange={upd("markup_pct")} data-testid="supplier-markup" />
            </div>
            <div>
              <label className="overline text-neutral-600 block mb-1">VAT %</label>
              <input type="number" min="0" step="1" className="vt-input" value={form.vat_rate} onChange={upd("vat_rate")} />
            </div>
            <div>
              <label className="overline text-neutral-600 block mb-1">Currency</label>
              <input className="vt-input" value={form.currency} onChange={upd("currency")} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.price_is_net} onChange={upd("price_is_net")} data-testid="supplier-net" />
            Supplier prices are NET (VAT is added on top)
          </label>
        </div>

        <div className="mb-4">
          <label className="overline text-neutral-600 block mb-1">Onboarding notes (internal)</label>
          <textarea className="vt-input font-mono text-xs" rows="4" value={form.notes} onChange={upd("notes")} />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-200">
          <button type="button" onClick={onClose} className="vt-btn-outline text-sm" disabled={saving}>Cancel</button>
          <button type="submit" className="vt-btn-orange text-sm" disabled={saving} data-testid="supplier-save">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ------- Credentials editor modal -------
function CredentialsEditor({ supplier, onClose }) {
  const [values, setValues] = useState(() => Object.fromEntries(SECRET_FIELDS.map((f) => [f.key, ""])));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const payload = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== ""));
    if (Object.keys(payload).length === 0) { toast.error("Enter at least one credential"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/suppliers/${supplier.id}/credentials`, payload);
      toast.success("Credentials saved (encrypted at rest, never returned in API responses)");
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const clearOne = async (fieldKey) => {
    if (!window.confirm(`Clear ${fieldKey}?`)) return;
    setSaving(true);
    try {
      await api.put(`/admin/suppliers/${supplier.id}/credentials`, { [fieldKey]: "" });
      toast.success(`${fieldKey} cleared`);
      onClose();
    } catch { toast.error("Clear failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="credentials-editor">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white max-w-lg w-full p-6 my-8 relative">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 p-2 hover:bg-neutral-100"><X size={20} /></button>
        <div className="flex items-center gap-2 mb-1">
          <Lock size={16} weight="duotone" className="text-[color:var(--vt-orange)]" />
          <div className="overline text-[color:var(--vt-orange)]">Credentials</div>
        </div>
        <h2 className="font-display font-black text-2xl tracking-tighter mb-3">{supplier.name}</h2>
        <p className="text-xs text-neutral-500 mb-5">
          Values are stored server-side under <code>doc.credentials.*</code>. They are <strong>never returned</strong>
          by any GET/list endpoint. Only the presence of each secret is shown as a lock badge on the supplier card.
          Leave a field blank to keep the current value. Clear a stored value by clicking Clear.
        </p>

        <div className="space-y-3 mb-5">
          {SECRET_FIELDS.map((f) => (
            <div key={f.key}>
              <div className="flex items-center justify-between mb-1">
                <label className="overline text-neutral-600">{f.label}</label>
                {supplier.has_credentials?.[f.key] && (
                  <button type="button" onClick={() => clearOne(f.key)} className="text-[10px] uppercase font-bold tracking-widest text-red-600 hover:underline" data-testid={`clear-${f.key}`}>
                    <LockOpen size={10} className="inline" /> Clear stored value
                  </button>
                )}
              </div>
              <input type="password" autoComplete="new-password"
                className="vt-input font-mono"
                placeholder={supplier.has_credentials?.[f.key] ? "•••• stored — leave blank to keep" : "enter to set"}
                value={values[f.key]}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                data-testid={`cred-${f.key}`}
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-200">
          <button type="button" onClick={onClose} className="vt-btn-outline text-sm" disabled={saving}>Cancel</button>
          <button type="submit" className="vt-btn-orange text-sm" disabled={saving} data-testid="creds-save">
            {saving ? "Saving…" : "Save credentials"}
          </button>
        </div>
      </form>
    </div>
  );
}
